/**
 * SHER-905 — the CSV primitive: how this app turns a table into a file.
 *
 * Promoted out of `pages/hospital/Request/surgicalListExport.ts`, where it was written for
 * SHER-462 and where the analytics tables could not reach it without importing a page module.
 * Nothing here decides WHAT a table exports; that is the caller's column list. What lives here
 * is only the part every caller gets wrong in the same way if each writes it again: RFC-4180
 * cell quoting, CRLF line endings, the UTF-8 BOM without which Excel renders the French
 * accents in a CHOM diagnosis as mojibake, and the zip of a column list into header + cells.
 *
 * TWO EXPORTS ARE KNOWINGLY NOT MIGRATED. `Billing/BillablesConfiguration.tsx` and
 * `Billing/BillableComboConfiguration.tsx` still hand-roll their own CSV, and should keep
 * doing so: those two files also IMPORT the file they emit, through their own `parseCSV`,
 * which reads `lines[0]` as the header row. A leading BOM would ride into the first header
 * name and break their own round-trip. They are a different problem — a data-interchange
 * format — from the human-facing Excel export this module serves.
 *
 * There is deliberately no xlsx here. The client declares no xlsx library (only the API has
 * node-xlsx), and a real xlsx would have to be produced server-side — which for the analytics
 * tables is impossible, because the API cannot read the encrypted patient fields those tables
 * render. See docs/features/analytics/README.md.
 */

/** RFC-4180 style cell escaping: quote cells containing comma/quote/newline. */
function escapeCsvCell(value: string): string {
    return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Join already-stringified cells into a CSV document: CRLF between lines, and a leading
 * UTF-8 BOM so Excel reads the file as UTF-8 instead of guessing a local codepage.
 *
 * The first line is not treated specially — a header is just the first row — so a caller
 * that wants a headerless file simply does not pass one.
 */
function toCsvDocument(lines: string[][]): string {
    return '\uFEFF' + lines.map((cells) => cells.map(escapeCsvCell).join(',')).join('\r\n');
}

/**
 * One exported column. `header` is the text the table displays, already translated by the
 * caller — this module never translates, because the header a user sees and the header they
 * get in the file must come from the same string.
 */
export interface ExportColumn<Row> {
    header: string;
    /**
     * The cell for this row. Returning null/undefined writes an empty cell, which is what a
     * spreadsheet can sum; returning the string "-" writes a dash, which is not.
     */
    value: (row: Row) => string | number | null | undefined;
}

/**
 * Serialise the rows a table is rendering, under the columns it is displaying.
 *
 * This knows nothing about any one table — no analytics, no surgery, just `Row`, a column
 * list and the null→empty rule — which is why it sits beside the primitive rather than in
 * either feature that calls it. Both do: the analytics tables through `ExportTableButton`,
 * and the surgical waiting list through `buildSurgicalListCsv`.
 *
 * A caller's export respects the active filters for a structural reason rather than a
 * defensive one: it is handed the SAME row array the table renders, which the data layer
 * already fetched under those filters. There is no second query here that could disagree
 * with the screen. Likewise it respects the displayed columns because the caller passes the
 * columns it displays — a hidden column is absent from the list, not filtered out afterwards.
 */
export function buildTableCsv<Row>(rows: Row[], columns: ExportColumn<Row>[]): string {
    const headerLine = columns.map((column) => column.header);
    const dataLines = rows.map((row) =>
        columns.map((column) => {
            const cell = column.value(row);
            return cell === null || cell === undefined ? '' : String(cell);
        }),
    );

    return toCsvDocument([headerLine, ...dataLines]);
}

/** Trigger a browser download of a CSV string (reuses the repo's blob-anchor pattern). */
export function downloadCsv(filename: string, csv: string): void {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
}
